import axios from 'axios';
import { TrackingOrder } from '../models/TrackingOrder';
import { TrackingOrderItem } from '../models/TrackingOrderItem';

const API_URL = process.env.REACT_APP_SERVER_URL + "/trackingorder";

const TrackingOrderService = {
    getAll: async (warehouseId?: string | null): Promise<TrackingOrder[]> => {
        const token = localStorage.getItem('gpcwstoken_kw');
        const params = warehouseId ? { warehouse_id: warehouseId } : {};
        const response = await axios.get(`${API_URL}/getAll`, {
            params,
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getStaleNoDeliveryManUpdate: async (warehouseId?: string | null, days?: number): Promise<TrackingOrder[]> => {
        const token = localStorage.getItem('gpcwstoken_kw');
        const params: Record<string, string | number> = {};
        if (warehouseId) params.warehouse_id = warehouseId;
        if (days != null) params.days = days;
        const response = await axios.get(`${API_URL}/getStaleNoDeliveryManUpdate`, {
            params,
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getById: async (id: number): Promise<TrackingOrder> => {
        const token = localStorage.getItem('gpcwstoken_kw');
        const response = await axios.get(`${API_URL}/getById/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getByDeliveryManToken: async (token: string): Promise<TrackingOrder> => {
        const response = await axios.get(`${API_URL}/getByDeliveryManToken/${token}`);
        return response.data;
    },

    getByCustomerToken: async (token: string): Promise<TrackingOrder> => {
        const response = await axios.get(`${API_URL}/getByCustomerToken/${token}`);
        return response.data;
    },

    getBySubDeliveryManToken: async (token: string): Promise<TrackingOrder> => {
        const response = await axios.get(`${API_URL}/getBySubDeliveryManToken/${token}`);
        return response.data;
    },

    getDeliveryManImages: async (id: number) => {
        const token = localStorage.getItem('gpcwstoken_kw');
        const response = await axios.get(`${API_URL}/getDeliveryManImages/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getSubDeliveryManImages: async (id: number) => {
        const token = localStorage.getItem('gpcwstoken_kw');
        const response = await axios.get(`${API_URL}/getSubDeliveryManImages/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    getCustomerImages: async (id: number) => {
        const token = localStorage.getItem('gpcwstoken_kw');
        const response = await axios.get(`${API_URL}/getCustomerImages/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    create: async (orderData: { customer_id: number; warehouse_id?: number | null; delivery_number?: string | null; remark?: string | null }, items: TrackingOrderItem[]) => {
        const token = localStorage.getItem('gpcwstoken_kw');
        const response = await axios.post(`${API_URL}/add`, { ...orderData, items }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    update: async (id: number, orderData: { customer_id: number; warehouse_id?: number | null; delivery_number?: string | null; remark?: string | null }, items: TrackingOrderItem[]) => {
        const token = localStorage.getItem('gpcwstoken_kw');
        const response = await axios.put(`${API_URL}/update/${id}`, { ...orderData, items }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    },

    delete: async (id: number): Promise<void> => {
        const token = localStorage.getItem('gpcwstoken_kw');
        await axios.delete(`${API_URL}/delete/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
    },

    createDeliveryManConfirmation: async (token: string, data: { driver_name: string; vehicle_no?: string; notes?: string }, image: File | null) => {
        const formData = new FormData();
        formData.append('driver_name', data.driver_name || '');
        if (data.vehicle_no) formData.append('vehicle_no', data.vehicle_no);
        if (data.notes) formData.append('notes', data.notes);
        if (image) formData.append('proven_image', image);
        const response = await axios.post(`${API_URL}/confirmDeliveryMan/${token}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    createSubDeliveryManConfirmation: async (token: string, data: { driver_name: string; vehicle_no?: string; notes?: string }, image: File | null) => {
        const formData = new FormData();
        formData.append('driver_name', data.driver_name || '');
        if (data.vehicle_no) formData.append('vehicle_no', data.vehicle_no);
        if (data.notes) formData.append('notes', data.notes);
        if (image) formData.append('proven_image', image);
        const response = await axios.post(`${API_URL}/confirmSubDeliveryMan/${token}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    createCustomerConfirmation: async (token: string, data: { recipient_name: string; notes?: string }, image: File | null, signatureImage: File | null) => {
        const formData = new FormData();
        formData.append('recipient_name', data.recipient_name || '');
        if (data.notes) formData.append('notes', data.notes);
        if (image) formData.append('proven_image', image);
        if (signatureImage) formData.append('signature_image', signatureImage);
        const response = await axios.post(`${API_URL}/confirmCustomer/${token}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export default TrackingOrderService;
